import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  cancelReviewRequest,
  invalidateReviewRequestQueries,
  restoreReviewRequest,
} from "@/lib/client/review";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { UpdateReviewRequestDialogContent } from "./update-review-request";

function ReviewRequestConfirmActionButton({
  id,
  onSuccess,
  triggerLabel,
  title,
  description,
  successMessage,
  mutationFn,
}: {
  id: string;
  onSuccess?: () => void;
  triggerLabel: string;
  title: string;
  description: string;
  successMessage: string;
  mutationFn: (payload: { data: { id: string } }) => Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutate, isPending } = useMutation({
    mutationFn,
    onSuccess: () => {
      toast.success(successMessage);
      invalidateReviewRequestQueries(queryClient, "user");
      onSuccess?.();
      setOpen(false);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger nativeButton={true} render={<Button size="sm">{triggerLabel}</Button>} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <Button size="sm" disabled={isPending} onClick={() => mutate({ data: { id } })}>
            <Loader2 className={clsx("animate-spin", isPending ? "block" : "hidden")} />
            确定
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const UserActions = {
  CancelReviewButton({ id, onSuccess }: { id: string; onSuccess?: () => void }) {
    return (
      <ReviewRequestConfirmActionButton
        id={id}
        onSuccess={onSuccess}
        triggerLabel="取消申请"
        title="取消申请"
        description="取消申请会归还消耗的积分。 取消后可以恢复，但是需要重新消耗积分。 恢复后，会重新排队"
        successMessage="取消申请成功，已归还积分"
        mutationFn={cancelReviewRequest}
      />
    );
  },

  RestorateReviewButton({ id, onSuccess }: { id: string; onSuccess?: () => void }) {
    return (
      <ReviewRequestConfirmActionButton
        id={id}
        onSuccess={onSuccess}
        triggerLabel="恢复申请"
        title="恢复申请"
        description="恢复申请会消耗积分，恢复后会重新排队"
        successMessage="恢复申请成功，已扣除积分"
        mutationFn={restoreReviewRequest}
      />
    );
  },

  UpdateReviewButton({ id, onSuccess }: { id: string; onSuccess?: () => void }) {
    const [open, setOpen] = useState(false);
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger nativeButton={true} render={<Button size="sm">修改申请</Button>} />
        <UpdateReviewRequestDialogContent
          open={open}
          id={id}
          onSuccess={() => {
            onSuccess?.();
            setOpen(false);
          }}
        />
      </Dialog>
    );
  },
};
